/**
 * Fleet System Row — compact operational row (90–110px). No energy flow, no mini dashboards.
 * Authority: Fleet Dashboard Spec §2–5. Status dot, name, SOC projection, net, sunrise, confidence, Why, last poll.
 * Sprint 1: Real why.primary, forecast_confidence display (high/medium/low inline), SOC → projected when breach_time.
 * Sprint 2: Breach countdown (Critical rows), stable row density (reduced padding, no confidence in minimal).
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EyeOff, MoreHorizontal, Star, Tag, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { FleetSystemWithSeverity } from '@/lib/fleet-severity';
import type { FleetSystem } from '@/data/fleet-types';
import { ProviderBadge } from '@/components/ui/ProviderBadge';

const BILLING_BADGE: Record<string, { label: string; className: string } | undefined> = {
  active: { label: 'Reseller', className: 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20' },
  pending: { label: 'Pending', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  past_due: { label: 'Past Due', className: 'bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20' },
  canceled: { label: 'Canceled', className: 'bg-destructive/10 text-destructive border-destructive/20' },
  trialing: { label: 'Trial', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
};

interface FleetSystemRowProps {
  system: FleetSystemWithSeverity;
  /** Minimal row (Stable section expanded) — name, SOC, net, sunrise only */
  minimal?: boolean;
  /** Called when user chooses Hide from actions menu */
  onHide?: (propertyId: string) => void;
  /** Called when user chooses Remove; implementer should show confirmation then delete */
  onRemove?: (propertyId: string, name: string) => void;
  /** When set, add Edit tags to actions menu */
  onEditTags?: () => void;
}

type ConfidenceLevel = 'low' | 'medium' | 'high';

function ConfidenceLabel({ level }: { level: ConfidenceLevel }) {
  const map = { high: 'High', medium: 'Medium', low: 'Low' } as const;
  const color =
    level === 'high'
      ? 'text-wt-green-primary'
      : level === 'medium'
        ? 'text-wt-amber-primary'
        : 'text-wt-text-muted';
  return <span className={cn('text-xs font-medium', color)}>{map[level]}</span>;
}

/** Format projected_breach_time for display (e.g. "3:40 AM"). */
function formatBreachTime(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return iso;
  }
}

/** Breach countdown: "Breach in 2h 14m". Re-renders every minute. */
function BreachCountdown({ breachTimeIso }: { breachTimeIso: string }) {
  const [label, setLabel] = useState('');
  useEffect(() => {
    const update = () => {
      const now = Date.now();
      const breach = new Date(breachTimeIso).getTime();
      const ms = breach - now;
      if (ms <= 0) {
        setLabel('Breach imminent');
        return;
      }
      const h = Math.floor(ms / (60 * 60 * 1000));
      const m = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
      setLabel(h > 0 ? `Breach in ${h}h ${m}m` : `Breach in ${m}m`);
    };
    update();
    const id = setInterval(update, 60 * 1000);
    return () => clearInterval(id);
  }, [breachTimeIso]);
  return <span className="text-[11px] font-medium text-wt-amber-primary shrink-0">{label}</span>;
}

export function FleetSystemRow({ system, minimal = false, onHide, onRemove, onEditTags }: FleetSystemRowProps) {
  const navigate = useNavigate();
  const { setPropertyScope } = useApp();
  const hasActions = onHide != null || onRemove != null || onEditTags != null;
  const {
    id,
    systemName,
    location,
    provider,
    online,
    lastPollAt,
    lastPollStatus,
    hasOvernightForecast,
    forecastRisk,
    gridMode,
    socPct,
    overnightKwhAtSunrise,
    pvKw,
    loadKw,
    battKw,
    severity,
    why,
    forecast_confidence,
    projected_breach_time,
    projected_min_soc_pct,
    timeToDepletionHours,
    lastTelemetryAt,
  } = system;
  const confidenceLevel: ConfidenceLevel =
    forecast_confidence === 'high' || forecast_confidence === 'medium' || forecast_confidence === 'low'
      ? forecast_confidence
      : 'low';

  const isGridPrimary = gridMode === 'grid_primary';
  /** Net draw: load minus PV (positive = drawing from battery/grid) */
  const netDrawKw = (loadKw ?? 0) - (pvKw ?? 0);
  const netSign = netDrawKw >= 0 ? '' : '-';
  const netAbs = Math.abs(netDrawKw);
  const pollLabel =
    lastPollAt == null
      ? 'Never polled'
      : lastPollStatus === 'success'
        ? `${formatDistanceToNow(new Date(lastPollAt), { addSuffix: true })}`
        : `Failed ${formatDistanceToNow(new Date(lastPollAt), { addSuffix: true })}`;

  /** Timestamp for "Data as of" — prefer last telemetry, fallback to last poll. Builds trust, explains stale. */
  const dataAsOfTs = lastTelemetryAt ?? lastPollAt;
  const dataAsOfLabel =
    dataAsOfTs == null
      ? 'No data yet'
      : (() => {
          try {
            return new Date(dataAsOfTs).toLocaleTimeString(undefined, {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            });
          } catch {
            return '—';
          }
        })();

  const dotColor =
    severity === 'critical' || severity === 'offline'
      ? 'bg-status-offline'
      : severity === 'watch'
        ? 'bg-wt-amber-primary'
        : 'bg-status-online';

  const handleClick = () => {
    setPropertyScope(id, systemName);
    navigate(`/app/property/${id}`);
  };

  const actionsDropdown = hasActions && (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground shrink-0"
          onClick={(e) => e.stopPropagation()}
          title="Actions"
        >
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {onHide != null && (
          <DropdownMenuItem onClick={() => onHide(id)}>
            <EyeOff className="w-4 h-4 mr-2" />
            Hide
          </DropdownMenuItem>
        )}
        {onRemove != null && (
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => onRemove(id, systemName)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Remove
          </DropdownMenuItem>
        )}
        {onEditTags != null && (
          <DropdownMenuItem onClick={onEditTags}>
            <Tag className="w-4 h-4 mr-2" />
            Edit tags
          </DropdownMenuItem>
        )}
        <DropdownMenuItem disabled>
          <Star className="w-4 h-4 mr-2" />
          Star (coming soon)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (minimal) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-border/50 bg-wt-bg-secondary px-3 py-1.5 text-left transition-colors hover:border-border hover:bg-wt-bg-tertiary/50 cursor-pointer min-h-[36px]'
        )}
      >
        <span className={cn('h-2 w-2 shrink-0 rounded-full', dotColor)} aria-hidden />
        <span className="min-w-0 flex-1 truncate font-medium text-wt-text-primary">{systemName}</span>
        <span className="text-xs text-wt-text-secondary">SOC {socPct}%</span>
        <span className="text-xs text-wt-text-secondary">Net {netSign}{netAbs.toFixed(1)} kW</span>
        <span className="text-xs text-wt-text-muted">
          Data as of {dataAsOfLabel}
        </span>
        <span className="text-xs text-wt-text-muted">
          {hasOvernightForecast && !isGridPrimary ? `~${overnightKwhAtSunrise} kWh` : '—'} · Sunrise
        </span>
        {actionsDropdown}
      </div>
    );
  }

  const whyText = why?.primary ?? 'System operating within expected range.';
  const breachTimeFormatted = projected_breach_time ? formatBreachTime(projected_breach_time) : null;
  const socProjection =
    breachTimeFormatted != null && projected_min_soc_pct != null
      ? ` → ${Math.round(projected_min_soc_pct)}% by ${breachTimeFormatted}`
      : hasOvernightForecast && !isGridPrimary
        ? ` → ~${overnightKwhAtSunrise} kWh by sunrise`
        : '';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'flex w-full flex-col gap-1 rounded-lg border border-border/50 bg-wt-bg-secondary px-3 py-2.5 text-left transition-colors hover:border-border hover:bg-wt-bg-tertiary/50 cursor-pointer',
        'min-h-[90px] max-h-[110px]',
        severity === 'critical' && 'border-l-[3px] border-l-status-offline',
        severity === 'watch' && 'border-l-[3px] border-l-amber-primary/60'
      )}
    >
      <div className="flex items-start gap-3">
        <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', dotColor)} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-wt-text-primary truncate">
              {systemName}
              {isGridPrimary ? ' (Grid-Tied)' : ' (Off-Grid)'}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              {severity === 'critical' && projected_breach_time && (
                <BreachCountdown breachTimeIso={projected_breach_time} />
              )}
              {system.billingMode === 'reseller' && system.resellerBillingStatus && BILLING_BADGE[system.resellerBillingStatus] && (
                <Badge variant="outline" className={cn('text-[10px] py-0 px-1.5 h-4', BILLING_BADGE[system.resellerBillingStatus]!.className)}>
                  {BILLING_BADGE[system.resellerBillingStatus]!.label}
                </Badge>
              )}
              <span className="text-[10px] font-medium uppercase text-wt-text-muted">
                <ProviderBadge provider={provider} />
              </span>
              {actionsDropdown}
            </div>
          </div>
          {location?.city || location?.state ? (
            <p className="text-xs text-wt-text-secondary mt-0.5">
              {[location.city, location.state].filter(Boolean).join(', ')}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs pl-5">
        <span className="text-wt-text-secondary">
          SOC: {socPct}%{socProjection}
        </span>
        <span className="text-wt-text-secondary">Net: {netSign}{netAbs.toFixed(1)} kW</span>
        {timeToDepletionHours != null && Number.isFinite(timeToDepletionHours) && (severity === 'critical' || severity === 'watch') && (
          <span className="text-wt-text-muted">~{timeToDepletionHours < 1 ? `${Math.round(timeToDepletionHours * 60)}m` : `${timeToDepletionHours.toFixed(1)}h`} to depletion</span>
        )}
        {hasOvernightForecast && !isGridPrimary && (
          <span className="text-wt-text-muted">Confidence: <ConfidenceLabel level={confidenceLevel} /></span>
        )}
      </div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[11px] text-wt-text-muted pl-5">
        <span>Data as of {dataAsOfLabel}</span>
        <span>Why: {whyText}</span>
        <span>Last Poll: {pollLabel}</span>
      </div>
    </div>
  );
}
